import React, { Fragment, useState } from 'react';
import * as Yup from 'yup';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CashManagementCrumbs } from 'front-office/constants/cash-management-crumbs';
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Typography,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { FormikErrors, useFormik } from 'formik';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useNavigate } from 'react-router-dom';
import ServiceList from 'front-office/components/cash-management/shared/service-list';
import OperatorList, { idAllOperators } from 'front-office/components/shared/operator-list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { Service } from 'shell/models/service';
import { PmiTooltipError } from 'shell/components/shared/tooltip-error';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
// import { openPdf } from 'shell/utils/open-pdf';
import { Operator } from 'shell/models/operator';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ItemVM } from 'shell/view-models/item';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { HttpResponse } from 'shell/models/http-response';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';

export interface CheckServiceForm {
  service?: string;
  serviceDesc?: string;
  operator?: string;
  operatorCode?: string;
  allOperators?: Operator[];
  extractionDateFrom?: DateTime;
  extractionDateTo?: DateTime;
  closeCash?: boolean;
  summary?: string;
}

const CashManagementCheckService = (): React.ReactElement => {
  const printSummary = useHttpClient<HttpResponse<ResponseCertificateGeneric>>(
    `${__CERTIFICATION_API_URL__}/certificazione/controllo-cassa/stampa-operatore-data`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const navigate = useNavigate();
  const { userProfile } = useUserProfile();

  const codeControlloCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';
  const codiceOperatoreCollegato = userProfile.datiLogin?.key?.userName || '';
  const nomeOperatoreCollegato = userProfile.datiLogin?.nome || '';
  const nomeServizioCollegato = userProfile.datiLogin?.profile?.servizio?.descrizioneBreve || '';
  const codiceServizioCollegato =
    userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio || '';

  /**
   * Per codici controllo cassa differenti ci sono differenti liste da mostrare nella select dei servizi
   *
   * Se l’Operatore ha il Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘0’,
   * il codice Operatore deve essere valorizzato con il Codice Operatore collegato e non è modificabile.
   *
   * Se operatore Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘1’ il Servizio deve essere valorizzato
   * con il codice del Servizio dell’Operatore collegato. Deve essere possibile l’opzione Tutti gli operatori
   *
   * Se operatore ha il Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘2’ il Servizio è digitabile, cambiano
   * le condizioni per estrarre la lista degli operatori(maggiori dettagli documentazione)
   * 
   * Operatore
   * Se l’Operatore ha il Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘0’ (Operatore di sportello),
   * il codice Operatore deve essere valorizzato con il Codice Operatore collegato ed è protetto e non è modificabile.
   * 
   * Se l’Operatore ha il Flag autorizzazione STAMPA CONTROLLO DI CASSA = ‘1’(Responsabile di Servizio) o ‘2’ (Servizio Cassa),
   * la lista drop-down viene valorizzata solo successivamente alla scelta del Servizio, leggendo la tabella MANAGEMENT.OPERATORE con

   */

  // const disableOperatorSelect = codeControlloCassa === '1';
  const defaultOperator = codeControlloCassa === '0' ? nomeOperatoreCollegato : '';
  const defaultCodeOperator = codeControlloCassa === '0' ? codiceOperatoreCollegato : '';

  const defaultService =
    codeControlloCassa === '0' || codeControlloCassa === '1' ? nomeServizioCollegato : '';

  const defaultServiceCode =
    codeControlloCassa === '0' || codeControlloCassa === '1' ? codiceServizioCollegato : '';

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });

  const onSubmit = (form: CheckServiceForm) => {
    (
      printSummary({
        data: {
          codiceServizio: form.service || undefined,
          descrizioneBreveServizio: form.serviceDesc || undefined,
          codiceOperatore: form.operatorCode === idAllOperators ? 'TUTTI' : form.operatorCode,
          nomeOperatore: form.operatorCode === idAllOperators ? '' : form.operator,
          dataEstrazioneDa: form.extractionDateFrom?.toFormat('yyyy-MM-dd') || undefined,
          dataEstrazioneA: form.extractionDateTo?.toFormat('yyyy-MM-dd') || undefined,
          riepilogo: form.summary === 'S',
        },
      }) as ResponsePromise<HttpResponse<ResponseCertificateGeneric>>
    ).then((response) => {
      setPdfModal({
        data: response?.data.payload?.contenutoPdfStampa || '',
        name: 'Gestione_Cassa',
        open: true,
      });
    });
  };
  const checkServiceForm = useFormik({
    initialValues: {
      service: defaultServiceCode,
      serviceDesc: defaultService,
      operator: defaultOperator,
      operatorCode: defaultCodeOperator,
      allOperators: [],
      extractionDateFrom: DateTime.now(),
      extractionDateTo: DateTime.now(),
      closeCash: false,
      summary: 'S',
    },
    onSubmit,
    validationSchema: Yup.object().shape({
      service: Yup.string().nullable().required('Il campo è obbligatorio'),
      operatorCode: Yup.string().nullable().required('Il campo è obbligatorio'),
      extractionDateFrom: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      extractionDateTo: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio')
        .when('extractionDateFrom', (extractionDateFrom, formvalidationSchema) =>
          extractionDateFrom && extractionDateFrom?.toString() !== 'Invalid Date'
            ? formvalidationSchema.min(
                extractionDateFrom,
                'La data deve essere maggiore o uguale della data Inizio'
              )
            : formvalidationSchema.nullable()
        ),
      summary: Yup.string().nullable().required('Il campo è obbligatorio'),
    }),
  });

  const onChangeServiceList = useEventCallback((service: Service | Service[], value?: string) => {
    if (!Array.isArray(service)) {
      checkServiceForm.setFieldValue('service', service.key.codiceServizio);
      checkServiceForm.setFieldValue('serviceDesc', service.descrizioneBreve);
      return;
    }
    checkServiceForm.setFieldValue('service', value);
  });

  const onChangeSummaryList = useEventCallback((event: SelectChangeEvent<string>) => {
    checkServiceForm.setFieldValue('summary', event.target.value);
  });

  const onChangeOperatorList = useEventCallback(
    (id?: string, item?: ItemVM | null, items?: Array<ItemVM>) => {
      if (id === idAllOperators && items && Array.isArray(items)) {
        checkServiceForm.setFieldValue('operator', '');
        checkServiceForm.setFieldValue('operatorCode', 'ALL');
        checkServiceForm.setFieldValue('allOperators', items);
      } else {
        checkServiceForm.setFieldValue('operatorCode', item?.id);
        checkServiceForm.setFieldValue('operator', item?.value);
        checkServiceForm.setFieldValue('allOperators', []);
      }
    }
  );

  return (
    <Fragment>
      <Breadcrumbs
        items={[...CashManagementCrumbs, { name: 'Controllo servizio/operatore/data' }]}
      />
      <Card raised className="pmi-my-3">
        <form onSubmit={checkServiceForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography gutterBottom variant="h5" component="h2">
              Controllo per servizio operatore data
            </Typography>
            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  {codeControlloCassa === '0' || codeControlloCassa === '1' ? (
                    <PmiTextField
                      id="service"
                      value={checkServiceForm.values.serviceDesc}
                      disabled
                      label="Servizio"
                      fullWidth
                    />
                  ) : (
                    <ServiceList
                      auth={codeControlloCassa === '2'}
                      allServices={codeControlloCassa === '2'}
                      value={checkServiceForm.values.service}
                      onChange={onChangeServiceList}
                      error={checkServiceForm.touched.service && !!checkServiceForm.errors.service}
                      errorMessage={checkServiceForm.errors.service as FormikErrors<string>}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={5}>
                  {codeControlloCassa === '0' ? (
                    <PmiTextField
                      id="operator"
                      value={checkServiceForm.values.operator}
                      disabled
                      fullWidth
                      label="Operatore"
                    />
                  ) : (
                    <OperatorList
                      auth={codeControlloCassa}
                      idService={checkServiceForm.values.service}
                      value={checkServiceForm.values.operatorCode}
                      onChange={onChangeOperatorList}
                      // disabled={disableOperatorSelect}
                      error={
                        checkServiceForm.touched.operatorCode &&
                        !!checkServiceForm.errors.operatorCode
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data inizio validità"
                    aria-label="Data inizio validità"
                    value={checkServiceForm.values.extractionDateFrom || null}
                    onChange={(date) => {
                      checkServiceForm.setFieldValue('extractionDateFrom', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="extractionDateFrom"
                        error={
                          checkServiceForm.touched.extractionDateFrom &&
                          !!checkServiceForm.errors.extractionDateFrom
                        }
                        helperText={
                          checkServiceForm.errors.extractionDateFrom as FormikErrors<string>
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data fine validità"
                    aria-label="Data fine validità"
                    value={checkServiceForm.values.extractionDateTo || null}
                    onChange={(date) => {
                      checkServiceForm.setFieldValue('extractionDateTo', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="extractionDateTo"
                        fullWidth
                        error={
                          checkServiceForm.touched.extractionDateTo &&
                          !!checkServiceForm.errors.extractionDateTo
                        }
                        helperText={
                          checkServiceForm.errors.extractionDateTo as FormikErrors<string>
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <PmiTooltipError
                    show={checkServiceForm.touched.summary && !!checkServiceForm.errors.summary}
                    message={checkServiceForm.errors.summary as FormikErrors<string>}
                  >
                    <PmiFormControl
                      fullWidth
                      error={checkServiceForm.touched.summary && !!checkServiceForm.errors.summary}
                    >
                      <InputLabel id="summary-label">Riepilogo</InputLabel>
                      <PmiSelect
                        labelId="summary-label"
                        id="summary"
                        name="summary"
                        value={checkServiceForm.values.summary}
                        label="Riepilogo"
                        onChange={onChangeSummaryList}
                      >
                        <MenuItem value="S">Sì</MenuItem>
                        <MenuItem value="N">No</MenuItem>
                      </PmiSelect>
                    </PmiFormControl>
                  </PmiTooltipError>
                </Grid>
              </Grid>
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <PmiButton
              id="submitForm"
              variant="contained"
              color="primary"
              onClick={() => checkServiceForm.handleSubmit()}
            >
              Conferma
            </PmiButton>
            <PmiButton
              id="goBack"
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                navigate('/sportello/gestione-cassa');
              }}
            >
              Annulla
            </PmiButton>
          </CardActions>
        </form>
      </Card>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Gestione cassa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
    </Fragment>
  );
};

CashManagementCheckService.displayName = 'Gestione cassa navigation menu';

export default CashManagementCheckService;
